import {
  BrandButton,
  ConnectWalletButton,
} from "../../../components/brand/BrandButton";
import { useAuth } from "../hooks/useAuth";
import { useAuthModal } from "../hooks/useAuthModal";
import { SignatureDialog } from "./SignatureDialog";
import {
  Copy,
  ExternalLink,
  RefreshCw,
  LogOut,
  Wallet,
  Shield,
  Check,
  ArrowUpRight,
  Pencil,
  QrCode,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { useTranslation } from "@/i18n";
import { useToast } from "@/hooks/useToast";
import { TransferDialog, ReceiveDialog } from "@/features/wallet";
import {
  useBrowserWallet,
  useBrowserWalletStore,
  useSolanaBalance,
  useSolanaTokenAccounts,
} from "@nullaone/wallet-react";
import { getExplorerAddressUrl } from "@/lib/solana/format";
import { COMMON_TOKEN_MINTS } from "@/config/constants";
import { useState, useMemo, useRef } from "react";

type TransferTokenId = "SOL" | "USDC" | "USDT";

export interface LoginButtonProps {
  className?: string;
  size?: "sm" | "default" | "lg" | "xl";
  showFullAddress?: boolean;
  withDropdown?: boolean;
}

/**
 * LoginButton Component
 *
 * Handles authentication flow:
 * 1. Not connected → Click opens browser wallet modal
 * 2. Connected but not authenticated → Click opens signature dialog
 * 3. Authenticated → Shows wallet address with logout dropdown
 */
export function LoginButton({
  className,
  size = "default",
  showFullAddress = false,
  withDropdown = true,
}: LoginButtonProps) {
  const { t } = useTranslation();
  const { isAuthenticated, wallet, logout } = useAuth();
  const {
    isDialogOpen,
    openAuth,
    closeDialog,
    confirmSign,
    step,
    challengeMessage,
    walletAddress,
    isLoading,
    error,
    retry,
  } = useAuthModal();

  // Wallet store state (for display in authenticated dropdown)
  const walletType = useBrowserWalletStore((state) => state.walletType);
  const walletName = useBrowserWalletStore((state) => state.walletName);
  const network = useBrowserWalletStore((state) => state.network);
  const walletId = useBrowserWalletStore((state) => state.walletId);
  const { renameWallet } = useBrowserWallet();
  const { error: showToastError } = useToast();

  // Balance hooks
  const {
    balance,
    isFetching: balanceLoading,
    refetch: refetchBalance,
  } = useSolanaBalance();
  const {
    tokenAccounts,
    isFetching: tokensFetching,
    refetch: refetchTokens,
  } = useSolanaTokenAccounts();

  // Derive USDC + USDT balances from tokenAccounts. Both are standard
  // 6-decimal stablecoins on mainnet; if the wallet has no token account
  // for a given mint, that token simply shows 0 (no error).
  const { usdcBalance, usdtBalance } = useMemo(() => {
    if (tokenAccounts.length === 0) {
      return { usdcBalance: 0, usdtBalance: 0 };
    }
    // wallet-react's TokenAccount.mint is a Kit Address (string).
    const usdcAccount = tokenAccounts.find(
      (ta) => ta.mint === COMMON_TOKEN_MINTS.USDC,
    );
    const usdtAccount = tokenAccounts.find(
      (ta) => ta.mint === COMMON_TOKEN_MINTS.USDT,
    );
    return {
      usdcBalance: usdcAccount?.uiAmount ?? 0,
      usdtBalance: usdtAccount?.uiAmount ?? 0,
    };
  }, [tokenAccounts]);
  const usdcDecimals = 6;
  const usdtDecimals = 6;

  const isRefreshing = balanceLoading || tokensFetching;

  // Copy address feedback state
  const [copied, setCopied] = useState(false);

  // Transfer dialog state
  const [transferOpen, setTransferOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [transferToken, setTransferToken] = useState<TransferTokenId>("SOL");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Inline rename of the LOCAL wallet label
  const [renaming, setRenaming] = useState(false);
  // Blur is the single commit path; Escape sets this so its blur cancels
  // instead of committing. (Pointer-driven menu closes never fire blur —
  // onOpenChange resets edit state instead.)
  const cancelRenameRef = useRef(false);

  const handleRename = async (value: string): Promise<void> => {
    const next = value.trim();
    setRenaming(false);
    if (!walletId || !next || next === (walletName ?? "")) return;
    try {
      await renameWallet(walletId, next);
    } catch {
      showToastError(t("wallet.rename.failed"));
    }
  };

  const formatAddress = (address: string): string => {
    if (showFullAddress) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Wallet icon based on type
  const WalletIcon = walletType === "ledger" ? Shield : Wallet;

  // Get display name for wallet type
  const getWalletDisplayName = (): string => {
    if (walletName) return walletName;
    if (walletType === "ledger") return "Ledger";
    return "Hot Wallet";
  };

  // Handle copy address
  const handleCopyAddress = () => {
    if (!wallet) return;
    navigator.clipboard.writeText(wallet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Handle view on explorer
  const handleViewExplorer = () => {
    if (!wallet) return;
    const url = getExplorerAddressUrl(wallet, network);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Handle opening transfer dialog from a token row
  const handleOpenTransfer = (token: TransferTokenId) => {
    setTransferToken(token);
    setDropdownOpen(false);
    // Small delay to let dropdown close animation finish
    setTimeout(() => setTransferOpen(true), 150);
  };

  // Handle opening the receive-QR dialog (same handoff dance as transfer)
  const handleOpenReceive = () => {
    setDropdownOpen(false);
    // Small delay to let dropdown close animation finish
    setTimeout(() => setReceiveOpen(true), 150);
  };

  const handleRefreshBalances = () => {
    refetchBalance();
    refetchTokens();
  };

  // Token row component for balance display (light theme).
  const TokenRow = ({
    symbol,
    tokenBalance,
    decimals,
    dotColor,
    tokenId,
  }: {
    symbol: string;
    tokenBalance: number;
    decimals: number;
    dotColor: string;
    tokenId: TransferTokenId;
  }) => (
    <div className="group flex items-center justify-between py-1.5 px-2 -mx-2 rounded-lg hover:bg-gray-100 transition-colors">
      <div className="flex items-center gap-2">
        <div className={`h-2 w-2 rounded-full ${dotColor}`} />
        <span className="text-sm text-text-light">{symbol}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-mono tabular-nums text-text-dark">
          {tokenBalance.toFixed(decimals <= 6 ? Math.min(decimals, 4) : 4)}
        </span>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleOpenTransfer(tokenId);
          }}
          className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-brand-primary/10 text-text-muted hover:text-brand-primary transition-all"
          aria-label={`${t("wallet.send")} ${symbol}`}
        >
          <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );

  // Shared SignatureDialog mount — kept in scope across all return branches
  // so the dialog stays mounted when `setAuthenticated` flips isAuthenticated
  // mid-flow (otherwise the dialog would unmount during the success animation).
  const signatureDialog = isDialogOpen ? (
    <SignatureDialog
      open={isDialogOpen}
      onOpenChange={(open) => {
        if (!open) closeDialog();
      }}
      step={step}
      challengeMessage={challengeMessage}
      walletAddress={walletAddress}
      error={error}
      isLoading={isLoading}
      onConfirm={confirmSign}
      onRetry={retry}
    />
  ) : null;

  // State 1: Authenticated - Show wallet address with dropdown (highest priority)
  if (isAuthenticated && wallet) {
    if (withDropdown) {
      return (
        <>
          <DropdownMenu
            open={dropdownOpen}
            onOpenChange={(open) => {
              setDropdownOpen(open);
              if (!open) {
                setRenaming(false);
                cancelRenameRef.current = false;
              }
            }}
          >
            <DropdownMenuTrigger asChild>
              <BrandButton
                variant="secondary"
                size={size}
                className={`bg-white hover:bg-gray-50 border border-border-card hover:border-text-muted text-text-dark font-medium transition-all ${className || ""}`}
                aria-label={t("auth.loggedInAs", { address: wallet })}
              >
                <div className="relative mr-2">
                  <WalletIcon
                    className="w-4 h-4 text-brand-primary"
                    aria-hidden="true"
                  />
                  <div className="absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-success" />
                </div>
                <span className="font-mono text-sm tabular-nums">
                  {formatAddress(wallet)}
                </span>
              </BrandButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-80 bg-white border border-border-card shadow-card-hover rounded-2xl p-0 overflow-hidden"
            >
              {/* Header: Wallet identity. A faint gold-tint band marks
                  this as a "valuable" section — same brand vocabulary
                  as the Featured / Place Bet top-bar. */}
              <div className="px-4 py-3.5 bg-gradient-to-r from-brand-accent/5 via-transparent to-brand-primary/5 border-b border-border-card">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-full bg-brand-primary/10">
                    <WalletIcon
                      className="h-4 w-4 text-brand-primary"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    {renaming ? (
                      <input
                        type="text"
                        defaultValue={walletName ?? getWalletDisplayName()}
                        aria-label={t("wallet.rename.placeholder")}
                        placeholder={t("wallet.rename.placeholder")}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.nativeEvent.isComposing) return;
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.currentTarget.blur();
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            cancelRenameRef.current = true;
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={(e) => {
                          if (cancelRenameRef.current) {
                            cancelRenameRef.current = false;
                            setRenaming(false);
                            return;
                          }
                          void handleRename(e.currentTarget.value);
                        }}
                        className="w-full min-w-0 rounded-md border border-brand-primary/40 bg-white px-2 py-0.5 text-sm font-semibold text-text-dark placeholder:text-text-muted focus:border-brand-primary focus:outline-none"
                      />
                    ) : (
                      <div className="flex items-center gap-1">
                        <p className="truncate text-sm font-semibold text-text-dark">
                          {getWalletDisplayName()}
                        </p>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setRenaming(true);
                          }}
                          aria-label={t("wallet.rename.edit")}
                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-text-muted hover:text-brand-primary hover:bg-gray-100 transition-colors"
                        >
                          <Pencil className="h-3 w-3" aria-hidden="true" />
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <div className="h-1.5 w-1.5 rounded-full bg-success" />
                      <p className="text-xs text-text-muted font-mono tabular-nums truncate">
                        {formatAddress(wallet)}
                      </p>
                      <div className="ml-auto flex items-center shrink-0">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCopyAddress();
                          }}
                          aria-label={
                            copied
                              ? t("common.copied")
                              : t("wallet.copyAddress")
                          }
                          className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:text-brand-primary hover:bg-gray-100 transition-colors"
                        >
                          {copied ? (
                            <Check
                              className="h-3.5 w-3.5 text-success"
                              aria-hidden="true"
                            />
                          ) : (
                            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleOpenReceive();
                          }}
                          aria-label={t("wallet.receive.menuItem")}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:text-brand-primary hover:bg-gray-100 transition-colors"
                        >
                          <QrCode className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleViewExplorer();
                          }}
                          aria-label={t("wallet.viewOnExplorer")}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:text-brand-primary hover:bg-gray-100 transition-colors"
                        >
                          <ExternalLink
                            className="h-3.5 w-3.5"
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Balance section */}
              <div className="mx-3 my-2 p-3 bg-bg-page rounded-xl border border-border-card/60">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[10px] font-bold text-text-muted uppercase tracking-[0.14em]">
                    {t("wallet.balance")}
                  </span>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRefreshBalances();
                    }}
                    disabled={isRefreshing}
                    className="p-1 rounded-md hover:bg-white transition-colors disabled:opacity-50"
                    aria-label={t("wallet.refresh")}
                  >
                    <RefreshCw
                      className={`h-3 w-3 text-text-muted ${isRefreshing ? "animate-spin" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                </div>
                <div className="space-y-0.5">
                  <TokenRow
                    symbol="SOL"
                    tokenBalance={balance ?? 0}
                    decimals={9}
                    dotColor="bg-brand-accent"
                    tokenId="SOL"
                  />
                  <TokenRow
                    symbol="USDC"
                    tokenBalance={usdcBalance}
                    decimals={usdcDecimals}
                    dotColor="bg-brand-primary"
                    tokenId="USDC"
                  />
                </div>
              </div>

              {/* Transfer button — primary brand CTA inside the dropdown. */}
              <div className="px-3 pb-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOpenTransfer("SOL");
                  }}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-brand-primary/10 hover:bg-brand-primary/15 border border-brand-primary/20 hover:border-brand-primary/30 text-brand-primary text-sm font-semibold transition-all"
                >
                  <ArrowUpRight className="h-4 w-4" />
                  {t("wallet.transfer")}
                </button>
              </div>

              {/* Disconnect — destructive in tone but quiet. */}
              <div className="px-3 pb-3">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    logout();
                  }}
                  disabled={false}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-danger/80 hover:text-danger hover:bg-danger/5 text-xs font-medium transition-colors disabled:opacity-50"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  {t("wallet.disconnect")}
                </button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* TransferDialog rendered outside dropdown */}
          <TransferDialog
            open={transferOpen}
            onOpenChange={setTransferOpen}
            defaultToken={transferToken}
            walletAddress={wallet}
            solBalance={balance}
            usdcBalance={usdcBalance}
            usdcDecimals={usdcDecimals}
            usdcMint={COMMON_TOKEN_MINTS.USDC}
            usdtBalance={usdtBalance}
            usdtDecimals={usdtDecimals}
            usdtMint={COMMON_TOKEN_MINTS.USDT}
            onTransferComplete={handleRefreshBalances}
          />
          <ReceiveDialog open={receiveOpen} onOpenChange={setReceiveOpen} />
          {signatureDialog}
        </>
      );
    }

    return (
      <>
        <div className={`flex items-center gap-3 ${className || ""}`}>
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-primary/10 border border-brand-primary/30"
            role="status"
            aria-label={t("auth.loggedInAs", { address: wallet })}
          >
            <Wallet className="w-4 h-4 text-brand-primary" aria-hidden="true" />
            <span className="text-sm font-mono text-brand-primary">
              {formatAddress(wallet)}
            </span>
          </div>
          <BrandButton
            onClick={logout}
            variant="ghost"
            size={size}
            aria-label={t("auth.logoutFromWallet")}
            className="text-text-muted hover:text-red-400"
          >
            <LogOut className="w-4 h-4 mr-2" aria-hidden="true" />
            {t("auth.logout")}
          </BrandButton>
        </div>
        {signatureDialog}
      </>
    );
  }

  // State 2: Not authenticated - Show "Connect Wallet" button
  return (
    <>
      <ConnectWalletButton
        onClick={() => openAuth()}
        size={size}
        className={className}
        aria-label={t("auth.connectWalletToSignIn")}
      >
        {t("auth.connectWallet")}
      </ConnectWalletButton>
      {signatureDialog}
    </>
  );
}

export function LoginButtonCompact(props: Omit<LoginButtonProps, "size">) {
  return <LoginButton {...props} size="sm" />;
}

export function LoginButtonLarge(props: Omit<LoginButtonProps, "size">) {
  return <LoginButton {...props} size="lg" />;
}
